import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Minesweeper.css";
import GameRoomBackButton from "../GameRoomBackButton";
import { submitSoloGameResult } from "../../../services/games/gameService";

const DIFFICULTIES = {
  beginner: { label: "Beginner", rows: 9, cols: 9, mines: 10 },
  intermediate: { label: "Intermediate", rows: 16, cols: 16, mines: 40 },
  expert: { label: "Expert", rows: 16, cols: 30, mines: 99 },
};

const THEMES = [
  { id: "hobbli", label: "Hobbli" },
  { id: "classic", label: "Classic" },
  { id: "arcade", label: "Arcade" },
];

const STATS_KEY = "hobbliMinesweeperStats";
const SETTINGS_KEY = "hobbliMinesweeperSettings";

function makeCell(row, col) {
  return {
    row,
    col,
    mine: false,
    revealed: false,
    flagged: false,
    adjacent: 0,
    exploded: false,
    wrongFlag: false,
  };
}

function createEmptyBoard(rows, cols) {
  return Array.from({ length: rows }, (_, row) =>
    Array.from({ length: cols }, (_, col) => makeCell(row, col))
  );
}

function cloneBoard(board) {
  return board.map((row) => row.map((cell) => ({ ...cell })));
}

function getNeighbors(row, col, rows, cols) {
  const result = [];
  for (let r = row - 1; r <= row + 1; r += 1) {
    for (let c = col - 1; c <= col + 1; c += 1) {
      if (r === row && c === col) continue;
      if (r >= 0 && r < rows && c >= 0 && c < cols) {
        result.push([r, c]);
      }
    }
  }
  return result;
}

function placeMines(board, mineCount, safeRow, safeCol) {
  const rows = board.length;
  const cols = board[0].length;
  const next = cloneBoard(board);

  const safeZone = new Set(
    [[safeRow, safeCol], ...getNeighbors(safeRow, safeCol, rows, cols)].map(
      ([r, c]) => `${r}:${c}`
    )
  );

  let candidates = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      if (!safeZone.has(`${row}:${col}`)) {
        candidates.push([row, col]);
      }
    }
  }

  // Fallback for unusually tiny boards.
  if (candidates.length < mineCount) {
    candidates = [];
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        if (!(row === safeRow && col === safeCol)) {
          candidates.push([row, col]);
        }
      }
    }
  }

  for (let i = candidates.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }

  candidates.slice(0, mineCount).forEach(([row, col]) => {
    next[row][col].mine = true;
  });

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      if (next[row][col].mine) continue;
      next[row][col].adjacent = getNeighbors(row, col, rows, cols).reduce(
        (count, [r, c]) => count + (next[r][c].mine ? 1 : 0),
        0
      );
    }
  }

  return next;
}

function revealArea(board, startRow, startCol) {
  const rows = board.length;
  const cols = board[0].length;
  const next = cloneBoard(board);
  const queue = [[startRow, startCol]];
  const seen = new Set();

  while (queue.length) {
    const [row, col] = queue.shift();
    const key = `${row}:${col}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const cell = next[row][col];
    if (cell.flagged || cell.revealed) continue;

    cell.revealed = true;

    if (!cell.mine && cell.adjacent === 0) {
      getNeighbors(row, col, rows, cols).forEach(([r, c]) => {
        const neighbor = next[r][c];
        if (!neighbor.revealed && !neighbor.flagged && !neighbor.mine) {
          queue.push([r, c]);
        }
      });
    }
  }

  return next;
}

function revealAllMines(board, explodedRow, explodedCol) {
  const next = cloneBoard(board);
  next.forEach((row) =>
    row.forEach((cell) => {
      if (cell.mine) {
        cell.revealed = true;
      }
      if (cell.flagged && !cell.mine) {
        cell.wrongFlag = true;
      }
    })
  );

  if (
    Number.isInteger(explodedRow) &&
    Number.isInteger(explodedCol) &&
    next[explodedRow]?.[explodedCol]
  ) {
    next[explodedRow][explodedCol].exploded = true;
  }

  return next;
}

function hasWon(board) {
  return board.every((row) =>
    row.every((cell) => cell.mine || cell.revealed)
  );
}

function loadJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function formatTime(seconds) {
  return String(Math.min(seconds, 999)).padStart(3, "0");
}

export default function Minesweeper({ currentUser }) {
  const savedSettings = useMemo(
    () =>
      typeof window !== "undefined"
        ? loadJson(SETTINGS_KEY, {})
        : {},
    []
  );

  const [difficulty, setDifficulty] = useState(
    savedSettings.difficulty && DIFFICULTIES[savedSettings.difficulty]
      ? savedSettings.difficulty
      : "beginner"
  );
  const [theme, setTheme] = useState(
    THEMES.some((item) => item.id === savedSettings.theme)
      ? savedSettings.theme
      : "classic"
  );
  const [flagMode, setFlagMode] = useState(false);
  const [board, setBoard] = useState(() => {
    const config =
      DIFFICULTIES[
        savedSettings.difficulty && DIFFICULTIES[savedSettings.difficulty]
          ? savedSettings.difficulty
          : "beginner"
      ];
    return createEmptyBoard(config.rows, config.cols);
  });
  const [status, setStatus] = useState("ready"); // ready | playing | won | lost
  const [started, setStarted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [stats, setStats] = useState(() =>
    typeof window !== "undefined"
      ? loadJson(STATS_KEY, {})
      : {}
  );

  const timerRef = useRef(null);
  const resultSubmittedRef = useRef(false);
  const longPressRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const config = DIFFICULTIES[difficulty];

  const flagsUsed = useMemo(
    () =>
      board.reduce(
        (total, row) =>
          total + row.filter((cell) => cell.flagged).length,
        0
      ),
    [board]
  );

  const minesRemaining = Math.max(0, config.mines - flagsUsed);

  const currentStats = stats[difficulty] || {
    played: 0,
    wins: 0,
    bestTime: null,
  };

  const saveStats = useCallback((nextStats) => {
    setStats(nextStats);
    try {
      window.localStorage.setItem(STATS_KEY, JSON.stringify(nextStats));
    } catch {
      // Storage is optional; gameplay should continue if unavailable.
    }
  }, []);

  const resetGame = useCallback(
    (nextDifficulty = difficulty) => {
      const nextConfig = DIFFICULTIES[nextDifficulty];
      setBoard(createEmptyBoard(nextConfig.rows, nextConfig.cols));
      setStatus("ready");
      setStarted(false);
      setSeconds(0);
      setFlagMode(false);
      resultSubmittedRef.current = false;
    },
    [difficulty]
  );

  useEffect(() => {
    if (status === "playing") {
      timerRef.current = window.setInterval(() => {
        setSeconds((value) => Math.min(999, value + 1));
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SETTINGS_KEY,
        JSON.stringify({ difficulty, theme })
      );
    } catch {
      // Settings persistence is optional.
    }
  }, [difficulty, theme]);

  const recordResult = useCallback(
    (won, finalSeconds) => {
      const previous = stats[difficulty] || {
        played: 0,
        wins: 0,
        bestTime: null,
      };

      const nextEntry = {
        played: previous.played + 1,
        wins: previous.wins + (won ? 1 : 0),
        bestTime:
          won &&
          (previous.bestTime == null || finalSeconds < previous.bestTime)
            ? finalSeconds
            : previous.bestTime,
      };

      saveStats({
        ...stats,
        [difficulty]: nextEntry,
      });

      if (currentUser?.id && !resultSubmittedRef.current) {
        resultSubmittedRef.current = true;
        submitSoloGameResult({
          gameKey: "minesweeper",
          result: won ? "win" : "loss",
          durationMs: Math.max(250, Number(finalSeconds || 0) * 1000),
          stats: { difficulty },
        }).catch((error) => {
          console.warn("Could not save Minesweeper result to Hobbli:", error);
        });
      }
    },
    [currentUser?.id, difficulty, saveStats, stats]
  );

  const toggleFlag = useCallback(
    (row, col) => {
      if (status === "won" || status === "lost") return;

      setBoard((previous) => {
        const cell = previous[row][col];
        if (cell.revealed) return previous;
        if (!cell.flagged && flagsUsed >= config.mines) return previous;

        const next = cloneBoard(previous);
        next[row][col].flagged = !next[row][col].flagged;
        return next;
      });
    },
    [config.mines, flagsUsed, status]
  );

  const revealCell = useCallback(
    (row, col) => {
      if (status === "won" || status === "lost") return;

      let workingBoard = board;

      if (!started) {
        workingBoard = placeMines(
          workingBoard,
          config.mines,
          row,
          col
        );
        setStarted(true);
        setStatus("playing");
      }

      const target = workingBoard[row][col];
      if (target.flagged || target.revealed) return;

      if (target.mine) {
        const lostBoard = revealAllMines(workingBoard, row, col);
        setBoard(lostBoard);
        setStatus("lost");
        recordResult(false, seconds);
        return;
      }

      const revealedBoard = revealArea(workingBoard, row, col);

      if (hasWon(revealedBoard)) {
        const wonBoard = cloneBoard(revealedBoard);
        wonBoard.forEach((boardRow) =>
          boardRow.forEach((cell) => {
            if (cell.mine) cell.flagged = true;
          })
        );
        setBoard(wonBoard);
        setStatus("won");
        recordResult(true, seconds);
        return;
      }

      setBoard(revealedBoard);
    },
    [
      board,
      config.mines,
      recordResult,
      seconds,
      started,
      status,
    ]
  );

  const chordReveal = useCallback(
    (row, col) => {
      if (status !== "playing") return;

      const cell = board[row][col];
      if (!cell.revealed || cell.adjacent === 0) return;

      const neighbors = getNeighbors(
        row,
        col,
        config.rows,
        config.cols
      );
      const flagged = neighbors.filter(
        ([r, c]) => board[r][c].flagged
      ).length;

      if (flagged !== cell.adjacent) return;

      let next = cloneBoard(board);

      for (const [r, c] of neighbors) {
        const neighbor = next[r][c];
        if (neighbor.flagged || neighbor.revealed) continue;

        if (neighbor.mine) {
          next = revealAllMines(next, r, c);
          setBoard(next);
          setStatus("lost");
          recordResult(false, seconds);
          return;
        }

        next = revealArea(next, r, c);
      }

      if (hasWon(next)) {
        next.forEach((boardRow) =>
          boardRow.forEach((item) => {
            if (item.mine) item.flagged = true;
          })
        );
        setBoard(next);
        setStatus("won");
        recordResult(true, seconds);
        return;
      }

      setBoard(next);
    },
    [
      board,
      config.cols,
      config.rows,
      recordResult,
      seconds,
      status,
    ]
  );

  const handleCellAction = (row, col) => {
    if (flagMode) {
      toggleFlag(row, col);
    } else if (board[row][col].revealed) {
      chordReveal(row, col);
    } else {
      revealCell(row, col);
    }
  };

  const handleContextMenu = (event, row, col) => {
    event.preventDefault();
    toggleFlag(row, col);
  };

  const handlePointerDown = (event, row, col) => {
    if (event.pointerType === "mouse") return;
    longPressTriggeredRef.current = false;
    window.clearTimeout(longPressRef.current);
    longPressRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      toggleFlag(row, col);
    }, 420);
  };

  const clearLongPress = () => {
    window.clearTimeout(longPressRef.current);
  };

  const handlePointerUp = (event) => {
    if (event.pointerType === "mouse") return;
    clearLongPress();

    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      event.preventDefault();
    }
  };

  const handleDifficultyChange = (event) => {
    const nextDifficulty = event.target.value;
    setDifficulty(nextDifficulty);
    resetGame(nextDifficulty);
  };

  const face =
    status === "lost"
      ? "😵"
      : status === "won"
      ? "😎"
      : status === "playing"
      ? "🙂"
      : "😊";

  const statusText =
    status === "won"
      ? "Board cleared — nice work!"
      : status === "lost"
      ? "Boom. Give it another shot."
      : status === "playing"
      ? "Clear every safe tile."
      : "Pick a tile to begin.";

  return (
    <div className={`minesweeper-page minesweeper-theme-${theme}`}>
      <section className="minesweeper-shell">

        <GameRoomBackButton standalone />

        <header className="minesweeper-hero">
          <div>
            <span className="minesweeper-eyebrow">
              Hobbli Game Room
            </span>

            <h1>Minesweeper</h1>

            <p>
              The classic logic game, rebuilt for desktop and mobile.
              Your first move is always safe.
            </p>
          </div>

          <div className="minesweeper-controls">
            <label>
              <span>Difficulty</span>
              <select
                value={difficulty}
                onChange={handleDifficultyChange}
              >
                {Object.entries(DIFFICULTIES).map(
                  ([key, item]) => (
                    <option key={key} value={key}>
                      {item.label}
                    </option>
                  )
                )}
              </select>
            </label>

            <label>
              <span>Theme</span>
              <select
                value={theme}
                onChange={(event) => setTheme(event.target.value)}
              >
                {THEMES.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </header>

        <div className="minesweeper-layout">
          <main className="minesweeper-game-card">
            <div className="minesweeper-scorebar">
              <div className="mine-counter" aria-label="Mines remaining">
                <span className="counter-icon">💣</span>
                <strong>{String(minesRemaining).padStart(3, "0")}</strong>
              </div>

              <button
                type="button"
                className="minesweeper-face-button"
                onClick={() => resetGame()}
                aria-label="Restart game"
                title="Restart"
              >
                {face}
              </button>

              <div className="mine-counter" aria-label="Elapsed time">
                <span className="counter-icon">⏱️</span>
                <strong>{formatTime(seconds)}</strong>
              </div>
            </div>

            <div className="minesweeper-mobile-tools">
              <button
                type="button"
                className={flagMode ? "active" : ""}
                onClick={() => setFlagMode((value) => !value)}
                aria-pressed={flagMode}
              >
                🚩 {flagMode ? "Flag Mode On" : "Flag Mode"}
              </button>
              <span>
                {flagMode
                  ? "Tap a tile to place or remove a flag"
                  : "Tap to reveal · long-press to flag"}
              </span>
            </div>

            <div
              className="minesweeper-board-scroll"
              aria-label={`${config.label} Minesweeper board`}
            >
              <div
                className="minesweeper-board"
                style={{
                  "--mine-cols": config.cols,
                }}
                role="grid"
              >
                {board.flat().map((cell) => {
                  const classes = [
                    "minesweeper-cell",
                    cell.revealed ? "revealed" : "covered",
                    cell.flagged ? "flagged" : "",
                    cell.exploded ? "exploded" : "",
                    cell.wrongFlag ? "wrong-flag" : "",
                    cell.revealed && cell.adjacent
                      ? `adjacent-${cell.adjacent}`
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  let content = "";
                  if (cell.wrongFlag) {
                    content = "✕";
                  } else if (cell.flagged) {
                    content = "🚩";
                  } else if (cell.revealed && cell.mine) {
                    content = "💣";
                  } else if (
                    cell.revealed &&
                    cell.adjacent > 0
                  ) {
                    content = cell.adjacent;
                  }

                  return (
                    <button
                      key={`${cell.row}-${cell.col}`}
                      type="button"
                      className={classes}
                      role="gridcell"
                      aria-label={
                        cell.flagged
                          ? "Flagged tile"
                          : cell.revealed
                          ? cell.mine
                            ? "Mine"
                            : cell.adjacent
                            ? `${cell.adjacent} adjacent mines`
                            : "Empty tile"
                          : "Covered tile"
                      }
                      onClick={() => handleCellAction(cell.row, cell.col)}
                      onDoubleClick={() =>
                        chordReveal(cell.row, cell.col)
                      }
                      onContextMenu={(event) =>
                        handleContextMenu(
                          event,
                          cell.row,
                          cell.col
                        )
                      }
                      onPointerDown={(event) =>
                        handlePointerDown(
                          event,
                          cell.row,
                          cell.col
                        )
                      }
                      onPointerUp={handlePointerUp}
                      onPointerCancel={clearLongPress}
                      onPointerLeave={clearLongPress}
                      disabled={
                        status === "won" || status === "lost"
                      }
                    >
                      {content}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className={`minesweeper-status status-${status}`}
              aria-live="polite"
            >
              <strong>{statusText}</strong>
              <span>
                {config.rows}×{config.cols} · {config.mines} mines
              </span>
              {(status === "won" || status === "lost") && (
                <button
                  type="button"
                  className="minesweeper-new-game-button"
                  onClick={() => resetGame()}
                >
                  New Game
                </button>
              )}
            </div>
          </main>

          <aside className="minesweeper-sidebar">
            <section className="minesweeper-panel">
              <h2>How to Play</h2>
              <p>
                Reveal every safe tile without opening a mine.
                Numbers tell you how many mines touch that tile.
              </p>
              <ul>
                <li>
                  <strong>Desktop:</strong> click to reveal,
                  right-click to flag.
                </li>
                <li>
                  <strong>Mobile:</strong> tap to reveal,
                  long-press to flag.
                </li>
                <li>
                  Use <strong>Flag Mode</strong> if you prefer
                  one-tap flagging on touch screens.
                </li>
                <li>
                  Open a numbered tile again when the correct
                  number of neighboring flags are placed to
                  clear the remaining neighbors.
                </li>
              </ul>
            </section>

            <section className="minesweeper-panel">
              <div className="panel-heading-row">
                <h2>{config.label} Stats</h2>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    const next = { ...stats };
                    delete next[difficulty];
                    saveStats(next);
                  }}
                >
                  Reset
                </button>
              </div>

              <div className="minesweeper-stat-grid">
                <div>
                  <span>Played</span>
                  <strong>{currentStats.played}</strong>
                </div>
                <div>
                  <span>Wins</span>
                  <strong>{currentStats.wins}</strong>
                </div>
                <div>
                  <span>Win Rate</span>
                  <strong>
                    {currentStats.played
                      ? `${Math.round(
                          (currentStats.wins /
                            currentStats.played) *
                            100
                        )}%`
                      : "—"}
                  </strong>
                </div>
                <div>
                  <span>Best</span>
                  <strong>
                    {currentStats.bestTime == null
                      ? "—"
                      : `${currentStats.bestTime}s`}
                  </strong>
                </div>
              </div>
            </section>

            <section className="minesweeper-panel minesweeper-tip">
              <h2>Quick Tip</h2>
              <p>
                A <strong>1</strong> means exactly one touching
                tile contains a mine. Use overlapping numbered
                clues together instead of guessing whenever you
                can.
              </p>
            </section>
          </aside>
        </div>
      </section>
    </div>
  );
}
